import { useEffect, useState } from 'react';
import notificationService from '../services/notificationService';
import toast from 'react-hot-toast';
import laravelEcho from '../services/laravelEcho';

/**
 * Custom hook for handling Laravel Echo notifications
 * Subscribes to user-specific channels and handles browser notifications
 * @param {number} userId - Primary user ID to subscribe to
 * @param {array} additionalUserIds - Additional user IDs to subscribe to (for testing)
 */
export const useEchoNotifications = (userId, additionalUserIds = []) => {
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [subscribedChannels, setSubscribedChannels] = useState([]);
  
  // Initialize Echo if not configured
  useEffect(() => {
    const echo = laravelEcho.getInstance();
    if (!echo) {
      const initialized = laravelEcho.initialize();
      if (!initialized) {
      }
    }
  }, []);
  
  // Get echo instance - always from the service to avoid conditional hook calls
  const echo = laravelEcho.getInstance();

  // Handle browser notification display
  const showBrowserNotification = async (data, source = 'notification') => {
    
    // Check if browser notifications are supported
    if ('Notification' in window) {
      // Request permission if it's default
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
      }
      
      // Show notification if permission is granted
      if (Notification.permission === 'granted') {
        try {
          // Use the data directly as it comes from Pusher
          const title = data.title || 'Test Notification 🔔';
          const message = data.body || data.message || 'This is a test notification from SecureRoom Admin';
          const type = data.type || 'info';
          
          const notificationResult = await notificationService.showTypedNotification(
            type,
            title,
            message,
            {
              onClick: () => {
                if (data.url || data.action_url) {
                  window.location.href = data.url || data.action_url;
                }
              }
            }
          );
          
          
          // Also show a toast notification for immediate feedback
          const toastMessage = message || title;
          switch(type) {
            case 'success':
              toast.success(toastMessage);
              break;
            case 'error':
              toast.error(toastMessage);
              break;
            case 'warning':
              toast(toastMessage, { icon: '⚠️' });
              break;
            default:
              toast(toastMessage, { icon: '🔔' });
          }
        } catch (error) {
          // Fallback to toast notification
          toast(data.body || data.message || data.title || 'New notification');
        }
      }
    } else {
      // Show toast notification as fallback
      toast(data.body || data.message || data.title || 'New notification', { icon: '🔔' });
    }
    
    // Add to notifications list
    setNotifications(prev => [{
      ...data,
      id: data.id || Date.now(),
      timestamp: new Date().toISOString(),
      source
    }, ...prev]);
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('echo:notification', { 
      detail: { ...data, source }
    }));
  };

  // Subscribe to channels and set up listeners
  useEffect(() => {
    // Get echo instance - either from hook or direct instance
    const echoInstance = echo || laravelEcho.getInstance();

    if (!echoInstance) {
      return;
    }

    if (!userId) {
      return;
    }

    // Subscribe to user-specific private channel
    const userChannelName = `App.User.${userId}`;

    // Don't leave all channels on every render - only in cleanup
    // This prevents continuous disconnect/reconnect cycles

    const channels = [];


    // IMPORTANT: Use private() for private channels, not channel()
    const userChannel = echoInstance.private(userChannelName);
    channels.push(`private-${userChannelName}`);

    // Check if channel subscription was successful
    userChannel.subscribed(() => {
    });

    userChannel.error((error) => {
      console.error(`❌ Error subscribing to private-${userChannelName}:`, error);
    });

    // Add debug listener to log ALL events on this channel
    // IMPORTANT: Private channels have 'private-' prefix in Pusher
    const pusherChannel = echoInstance.connector.pusher.channel(`private-${userChannelName}`);
    if (pusherChannel) {
      pusherChannel.bind_global((eventName, data) => {
      });
    }

    // Set up listeners for user channel
    // Only listen to one format to avoid duplicates
    userChannel
      .listen('.TestPROD', (eventData) => {
        // The event data is the notification itself
        showBrowserNotification(eventData, 'TestPROD');
      })
      .listen('notification', (data) => {
        showBrowserNotification(data, 'user-notification');
      })
      .listen('.notification', (data) => {
        showBrowserNotification(data, 'user-notification-dot');
      })
      .listen('NotificationCreated', (data) => {
        // Handle Laravel notification data structure
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'NotificationCreated');
      })
      .listen('.NotificationCreated', (data) => {
        // Handle Laravel notification data structure
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'NotificationCreated-dot');
      })
      .listen('notification-created', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'notification-created');
      })
      .listen('.notification-created', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'notification-created-dot');
      })
      .listen('test-notification', (data) => {
        showBrowserNotification(data, 'test-notification');
      })
      .listen('.test-notification', (data) => {
        showBrowserNotification(data, 'test-notification-dot');
      })
      .listen('.dataroom-invite', (data) => {
        const notificationData = {
          title: '📁 New Dataroom Invitation',
          body: `You've been invited to ${data.dataroom_name || 'a dataroom'}`,
          type: 'info',
          url: '/my-invitations',
          ...data
        };
        showBrowserNotification(notificationData, 'dataroom-invite');
      })
      .listen('.subscription-update', (data) => {
        const notificationData = {
          title: '💳 Subscription Update',
          body: data.body || data.message || 'Your subscription has been updated',
          type: data.type || 'info',
          url: '/subscriptions',
          ...data
        };
        showBrowserNotification(notificationData, 'subscription-update');
      })
      .listen('.file-shared', (data) => {
        const notificationData = {
          title: '📄 File Shared',
          body: `${data.sender_name || 'Someone'} shared "${data.file_name || 'a file'}" with you`,
          type: 'info',
          url: data.dataroom_id ? `/datarooms/${data.dataroom_id}/files` : null,
          ...data
        };
        showBrowserNotification(notificationData, 'file-shared');
      })
      .listen('file-shared', (data) => {
        const notificationData = {
          title: '📄 File Shared',
          body: `${data.sender_name || 'Someone'} shared "${data.file_name || 'a file'}" with you`,
          type: 'info',
          url: data.dataroom_id ? `/datarooms/${data.dataroom_id}/files` : null,
          ...data
        };
        showBrowserNotification(notificationData, 'file-shared');
      })
      .listen('file-uploaded', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || '📤 File Uploaded',
          body: data.body || data.message || data.data?.body || data.data?.message || 'A new file has been uploaded',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'file-uploaded');
      })
      .listen('.file-uploaded', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || '📤 File Uploaded',
          body: data.body || data.message || data.data?.body || data.data?.message || 'A new file has been uploaded',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'file-uploaded-dot');
      })
      .listen('FileUploaded', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || '📤 File Uploaded',
          body: data.body || data.message || data.data?.body || data.data?.message || 'A new file has been uploaded',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'FileUploaded');
      })
      .listen('.FileUploaded', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || '📤 File Uploaded',
          body: data.body || data.message || data.data?.body || data.data?.message || 'A new file has been uploaded',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'FileUploaded-dot');
      });

    // Subscribe to additional user channels (for testing cross-user notifications)
    if (additionalUserIds && additionalUserIds.length > 0) {
      additionalUserIds.forEach(additionalUserId => {
        const additionalChannelName = `App.User.${additionalUserId}`;

        // Use private() for private channels
        const additionalChannel = echoInstance.private(additionalChannelName);
        channels.push(`private-${additionalChannelName}`);

        // Only listen to one format to avoid duplicates
        additionalChannel
          .listen('.TestPROD', (eventData) => {
            // The event data is the notification itself
            showBrowserNotification(eventData, `TestPROD-user-${additionalUserId}`);
          });
      });
    }

    // Subscribe to global notifications channel
    const globalChannel = echoInstance.channel('global-notifications');
    channels.push('global-notifications');

    // Add debug listener for global channel
    const globalPusherChannel = echoInstance.connector.pusher.channel('global-notifications');
    if (globalPusherChannel) {
      globalPusherChannel.bind_global((eventName, data) => {
      });
    }

    // Only listen to one format to avoid duplicates
    globalChannel
      .listen('.TestPROD', (data) => {
        showBrowserNotification(data, 'TestPROD-global');
      })
      .listen('.notification', (data) => {
        showBrowserNotification(data, 'global-notification');
      })
      .listen('NotificationCreated', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'NotificationCreated-global');
      })
      .listen('.NotificationCreated', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'NotificationCreated-global-dot');
      })
      .listen('.announcement', (data) => {
        const notificationData = {
          title: '📢 ' + (data.title || 'Announcement'),
          body: data.body || data.message || '',
          type: 'info',
          requireInteraction: true,
          ...data
        };
        showBrowserNotification(notificationData, 'announcement');
      })
      .listen('.test-notification', (data) => {
        showBrowserNotification(data, 'test-notification-global');
      });

    // Subscribe to test notifications channel
    const testChannel = echoInstance.channel('test-notifications');
    channels.push('test-notifications');

    // Only listen to one format to avoid duplicates
    testChannel
      .listen('.TestPROD', (data) => {
        showBrowserNotification(data, 'TestPROD-test');
      })
      .listen('.test', (data) => {
        showBrowserNotification(data, 'test-event');
      })
      .listen('.notification', (data) => {
        showBrowserNotification(data, 'test-channel-notification');
      })
      .listen('NotificationCreated', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'NotificationCreated-test');
      })
      .listen('.NotificationCreated', (data) => {
        const notificationData = {
          title: data.title || data.data?.title || 'New Notification',
          body: data.body || data.message || data.data?.body || data.data?.message || '',
          type: data.type || data.data?.type || 'info',
          url: data.url || data.action_url || data.data?.url || data.data?.action_url || null,
          ...data
        };
        showBrowserNotification(notificationData, 'NotificationCreated-test-dot');
      });

    setSubscribedChannels(channels);

    // Cleanup function to leave channels
    return () => {
      // Use the correct channel name format for private channels
      echoInstance.leave(`private-${userChannelName}`);

      // Leave additional user channels
      if (additionalUserIds && additionalUserIds.length > 0) {
        additionalUserIds.forEach(additionalUserId => {
          echoInstance.leave(`private-App.User.${additionalUserId}`);
        });
      }

      echoInstance.leave('global-notifications');
      echoInstance.leave('test-notifications');
      setSubscribedChannels([]);
    };
  }, [userId]); // Only re-run when userId changes

  // Monitor connection status
  useEffect(() => {
    const echoInstance = echo || laravelEcho.getInstance();
    
    if (echoInstance) {
      const checkConnection = () => {
        const pusher = echoInstance.connector?.pusher;
        if (pusher) {
          const state = pusher.connection.state;
          setIsConnected(state === 'connected');
        }
      };

      // Check initial connection
      checkConnection();

      // Set up connection event listeners
      const pusher = echoInstance.connector?.pusher;
      if (pusher) {
        pusher.connection.bind('connected', () => {
          setIsConnected(true);
        });

        pusher.connection.bind('disconnected', () => {
          setIsConnected(false);
        });

        pusher.connection.bind('error', (error) => {
          setIsConnected(false);
        });
      }
    }
  }, [echo]);

  // Request notification permission on mount
  useEffect(() => {
    const requestPermission = async () => {
      if ('Notification' in window && Notification.permission === 'default') {
        try {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            toast.success('Browser notifications enabled!');
          }
        } catch (error) {
        }
      }
    };

    // Request permission after a short delay
    const timer = setTimeout(requestPermission, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Function to manually ensure subscription
  const ensureSubscription = async () => {
    if (!userId) return false;
    
    const { ensureUserChannelSubscribed } = await import('../services/laravelEcho');
    return await ensureUserChannelSubscribed(userId);
  };

  return {
    notifications,
    isConnected,
    subscribedChannels,
    clearNotifications: () => setNotifications([]),
    removeNotification: (id) => setNotifications(prev => prev.filter(n => n.id !== id)),
    ensureSubscription
  };
};

export default useEchoNotifications;